import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Menu,
  Card,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Tabs,
  message,
  Modal,
  Tooltip,
  Badge,
  Divider,
  Empty,
  Spin,
  Alert,
  Checkbox,
  Upload
} from 'antd';
import {
  CloudUploadOutlined,
  FileTextOutlined,
  BarChartOutlined,
  QuestionCircleOutlined,
  CalendarOutlined,
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  FolderOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  InboxOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  ProjectOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { loamsEngineerApi } from '@/api/loamsEngineerApi';
import phoenicianLogo from '../../assets/PhoenicianLogo.png';
import CompletionCertificate from './CompletionCertificate';
import BulkCertificateGenerator from './BulkCertificateGenerator';
import WorkProgress from './WorkProgress';
import './EngineerDashboard.css';

const { Header, Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Dragger } = Upload;

// Color constants
const PHOENICIAN_GOLD = '#A2732E';
const PHOENICIAN_DARK = '#1a1a2e';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StatusTag = ({ exists, size = 'default' }) => (
  <Tag 
    icon={exists ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
    color={exists ? 'success' : 'error'}
    style={{ fontSize: size === 'small' ? '11px' : '13px' }}
  >
    {exists ? 'Uploaded' : 'Missing'}
  </Tag>
);

const ServiceTypeBadge = ({ serviceType }) => {
  if (!serviceType) return null;
  
  const type = serviceType.toUpperCase();
  if (type.includes('SWIMMING') || type.includes('POOL')) {
    return <Tag color="blue" icon={<SafetyCertificateOutlined />}>Swimming Pool</Tag>;
  }
  if (type.includes('LANDSCAPE') || type.includes('IRRIGATION')) {
    return <Tag color="green" icon={<SafetyCertificateOutlined />}>Landscaping</Tag>;
  }
  return <Tag color="default">{serviceType}</Tag>;
};

const GPT4HealthIndicator = ({ status }) => (
  <Tooltip title={status ? 'AI Validation Ready' : 'AI Validation Unavailable'}>
    <Badge 
      status={status ? 'success' : 'error'} 
      text={<Text type="secondary" style={{ fontSize: '12px' }}>AI Validation</Text>}
    />
  </Tooltip>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EngineerDashboard = () => {
  // State Management
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('single');
  const [loading, setLoading] = useState(false);
  
  // Data State
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState('');
  const [agreementDetails, setAgreementDetails] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [payees, setPayees] = useState([]);
  const [selectedPayee, setSelectedPayee] = useState('');
  
  // Bulk Generation State
  const [bulkMonth, setBulkMonth] = useState('');
  const [bulkAgreements, setBulkAgreements] = useState([]);
  const [selectedBulkItems, setSelectedBulkItems] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkStatistics, setBulkStatistics] = useState(null);
  
  // Bulk Generation Modal State
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  
  // Upload State
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [gpt4Health, setGpt4Health] = useState(false);
  
  // Validation State (NEW)
  const [pendingFile, setPendingFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  
  // Overview Statistics
  const [overviewStats, setOverviewStats] = useState({
    totalPOs: 0,
    totalClusters: 0,
    totalCertificates: 0,
    completionRate: 0
  });

  // ============================================================================
  // TAB CHANGE HANDLER (AUTO-COLLAPSE ON MOBILE)
  // ============================================================================

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Auto-collapse sidebar on mobile/tablet
    if (window.innerWidth < 1024) {
      setCollapsed(true);
    }
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    initializeDashboard();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchClusters();
      fetchPayees();
      fetchOverviewStats();
    }
  }, [selectedYear]);

  useEffect(() => {
    if (selectedCluster) {
      fetchAgreements();
    }
  }, [selectedCluster, selectedYear]);

  useEffect(() => {
    if (selectedAgreement) {
      fetchAgreementDetails();
      fetchCertificates();
    }
  }, [selectedAgreement]);

  const initializeDashboard = async () => {
    setLoading(true);
    try {
      // Fetch available years
      const yearsResponse = await loamsEngineerApi.getAvailableYears();
      if (yearsResponse.success) {
        setYears(yearsResponse.years);
        if (yearsResponse.years.length > 0) {
          setSelectedYear(yearsResponse.current_year.toString());
        }
      }
      
      // Check GPT-4 health
      try {
        const healthResponse = await loamsEngineerApi.gpt4Validation.checkHealth();
        setGpt4Health(healthResponse.api_working);
      } catch {
        setGpt4Health(false);
      }
    } catch (error) {
      console.error('Dashboard initialization error:', error);
      message.error('Failed to initialize dashboard');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchClusters = async () => {
    try {
      const response = await loamsEngineerApi.getClusters(selectedYear);
      if (response.success) {
        setClusters(response.clusters);
      }
    } catch (error) {
      console.error('Error fetching clusters:', error);
      message.error('Failed to fetch clusters');
    }
  };

  const fetchPayees = async () => {
    try {
      const response = await loamsEngineerApi.getUniquePayees(selectedYear);
      if (response.success) {
        setPayees(response.payees);
      }
    } catch (error) {
      console.error('Error fetching payees:', error);
    }
  };

  const fetchOverviewStats = async () => {
    try {
      const response = await loamsEngineerApi.getDashboardOverview(selectedYear);
      if (response.success) {
        setOverviewStats(response.statistics);
      }
    } catch (error) {
      console.error('Error fetching overview stats:', error);
    }
  };

  const fetchAgreements = async () => {
    if (!selectedCluster) return;
    
    try {
      const response = await loamsEngineerApi.getClusterAgreements(selectedCluster, selectedYear);
      if (response.success) {
        setAgreements(response.agreements);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
      message.error('Failed to fetch agreements');
    }
  };

  const fetchAgreementDetails = async () => {
    if (!selectedCluster || !selectedAgreement) return;
    
    try {
      const response = await loamsEngineerApi.getAgreementDetails(selectedCluster, selectedAgreement);
      if (response.success) {
        setAgreementDetails(response.data);
        setMonths(response.data.months || []);
      }
    } catch (error) {
      console.error('Error fetching agreement details:', error);
    }
  };

  const fetchCertificates = async () => {
    if (!selectedCluster || !selectedAgreement) return;
    
    try {
      const response = await loamsEngineerApi.certificates.list(selectedCluster, selectedAgreement);
      if (response.success) {
        setCertificates(response.certificates);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  // ============================================================================
  // BULK GENERATION FUNCTIONS
  // ============================================================================

  const fetchBulkAgreements = async (month) => {
    if (!month) return;
    
    setBulkLoading(true);
    try {
      const response = await loamsEngineerApi.getAgreementsByMonth(
        month,
        selectedPayee || null,
        null
      );
      
      if (response.success) {
        setBulkAgreements(response.agreements);
        setBulkStatistics(response.statistics);
        setSelectedBulkItems([]);
      }
    } catch (error) {
      console.error('Error fetching bulk agreements:', error);
      message.error('Failed to fetch agreements for selected month');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkMonthChange = (value) => {
    setBulkMonth(value);
    fetchBulkAgreements(value);
  };

  const handleBulkSelectAll = (e) => {
    if (e.target.checked) {
      const missingItems = bulkAgreements
        .filter(a => !a.certificate_exists)
        .map(a => `${a.cluster}-${a.ref_no}`);
      setSelectedBulkItems(missingItems);
    } else {
      setSelectedBulkItems([]);
    }
  };

  const handleBulkItemSelect = (key, checked) => {
    if (checked) {
      setSelectedBulkItems([...selectedBulkItems, key]);
    } else {
      setSelectedBulkItems(selectedBulkItems.filter(k => k !== key));
    }
  };

  const getSelectedAgreementsData = useCallback(() => {
    return bulkAgreements.filter(a => 
      selectedBulkItems.includes(`${a.cluster}-${a.ref_no}`)
    );
  }, [bulkAgreements, selectedBulkItems]);

  const openBulkGenerationModal = () => {
    if (selectedBulkItems.length === 0) {
      message.warning('Please select at least one agreement');
      return;
    }
    setBulkModalVisible(true);
  };

  const closeBulkModal = () => {
    setBulkModalVisible(false);
  };

  const handleBulkGenerationComplete = (results) => {
    console.log('[BULK] Generation complete:', results);
    if (bulkMonth) {
      fetchBulkAgreements(bulkMonth);
    }
    setSelectedBulkItems([]);
  };

  // ============================================================================
  // UPLOAD FUNCTIONS WITH CONFIRMATION
  // ============================================================================

  const handleFileUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    if (!selectedCluster || !selectedAgreement || !selectedMonth) {
      message.error('Please select cluster, agreement, and month first');
      onError(new Error('Missing required fields'));
      return;
    }
    
    setUploading(true);
    setPendingFile({ file, onSuccess, onError });
    
    try {
      // Step 1: Validate certificate first
      const formData = new FormData();
      formData.append('file', file);
      formData.append('cluster', selectedCluster);
      formData.append('agreement_ref', selectedAgreement);
      formData.append('month', selectedMonth);
      
      const validateResponse = await loamsEngineerApi.certificates.upload(formData);
      
      // Check if it's a validation response (not actual upload yet)
      if (validateResponse.validation_failed) {
        // Show validation failure modal
        showValidationFailureModal(validateResponse.validation_details);
        onError(new Error('Validation failed'));
        setUploading(false);
        setPendingFile(null);
        setFileList([]);
        return;
      }
      
      // If validation passed, show confirmation modal
      if (validateResponse.success && !validateResponse.invoice) {
        // This is just validation success, show confirmation
        setValidationResult(validateResponse.validation_details);
        showUploadConfirmationModal(formData, onSuccess, onError);
      } else {
        // Direct upload success (no validation)
        handleUploadSuccess(validateResponse, onSuccess);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      
      if (error.response?.data?.validation_failed) {
        showValidationFailureModal(error.response.data.validation_details);
      } else if (error.response?.data?.certificate_exists) {
        message.warning('A certificate already exists for this month');
      } else {
        message.error(error.response?.data?.error || error.message || 'Failed to upload certificate');
      }
      
      onError(error);
      setUploading(false);
      setPendingFile(null);
      setFileList([]);
    }
  };

  const showValidationFailureModal = (details) => {
    const extracted = details.extracted_values || {};
    
    Modal.error({
      title: 'Certificate Validation Failed',
      width: 520,
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div style={{ marginTop: 16 }}>
          <p>The uploaded PDF does not match the selected criteria:</p>
          
          <div style={{ 
            background: '#fff2f0', 
            border: '1px solid #ffccc7',
            borderRadius: 8,
            padding: 16,
            margin: '16px 0'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              {details.cluster_match ? (
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8, marginTop: 4 }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8, marginTop: 4 }} />
              )}
              <div>
                <Text strong>Cluster</Text>
                {details.cluster_match ? (
                  <Text type="success" style={{ display: 'block' }}>Match</Text>
                ) : (
                  <div>
                    <Text type="secondary" style={{ display: 'block' }}>Expected: <Text strong>{selectedCluster}</Text></Text>
                    <Text type="danger" style={{ display: 'block' }}>Found: <Text strong>{extracted.cluster || 'Unknown'}</Text></Text>
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              {details.po_match ? (
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8, marginTop: 4 }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8, marginTop: 4 }} />
              )}
              <div>
                <Text strong>PO/Agreement</Text>
                {details.po_match ? (
                  <Text type="success" style={{ display: 'block' }}>Match</Text>
                ) : (
                  <div>
                    <Text type="secondary" style={{ display: 'block' }}>Expected: <Text strong>{selectedAgreement}</Text></Text>
                    <Text type="danger" style={{ display: 'block' }}>Found: <Text strong>{extracted.po || 'Unknown'}</Text></Text>
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              {details.month_match ? (
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8, marginTop: 4 }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8, marginTop: 4 }} />
              )}
              <div>
                <Text strong>Month</Text>
                {details.month_match ? (
                  <Text type="success" style={{ display: 'block' }}>Match</Text>
                ) : (
                  <div>
                    <Text type="secondary" style={{ display: 'block' }}>Expected: <Text strong>{selectedMonth}</Text></Text>
                    <Text type="danger" style={{ display: 'block' }}>Found: <Text strong>{extracted.month || 'Unknown'}</Text></Text>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {details.confidence && (
            <p style={{ color: '#666', fontSize: 12 }}>AI Confidence: {Math.round(details.confidence * 100)}%</p>
          )}
          
          <Alert
            type="warning"
            message="Please upload the correct certificate that matches the selected cluster, PO/agreement reference, and month."
            style={{ marginTop: 16 }}
          />
        </div>
      ),
      okText: 'Got it',
      okButtonProps: { type: 'primary' }
    });
  };

  const showUploadConfirmationModal = (formData, onSuccess, onError) => {
    Modal.confirm({
      title: 'Confirm Certificate Upload',
      width: 500,
      icon: <ExclamationCircleOutlined style={{ color: PHOENICIAN_GOLD }} />,
      content: (
        <div>
          <Alert
            message="Certificate validated successfully!"
            description="The AI has verified that this certificate matches the selected criteria."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <div style={{ 
            background: '#f6ffed', 
            padding: 16, 
            borderRadius: 8,
            border: '1px solid #b7eb8f'
          }}>
            <Row gutter={[8, 8]}>
              <Col span={8}><Text type="secondary">Cluster:</Text></Col>
              <Col span={16}><Text strong>{selectedCluster}</Text></Col>
              
              <Col span={8}><Text type="secondary">PO/Agreement:</Text></Col>
              <Col span={16}><Text strong>{selectedAgreement}</Text></Col>
              
              <Col span={8}><Text type="secondary">Month:</Text></Col>
              <Col span={16}><Text strong>{selectedMonth}</Text></Col>
              
              {validationResult?.confidence && (
                <>
                  <Col span={8}><Text type="secondary">AI Confidence:</Text></Col>
                  <Col span={16}>
                    <Text strong style={{ color: '#52c41a' }}>
                      {Math.round(validationResult.confidence * 100)}%
                    </Text>
                  </Col>
                </>
              )}
            </Row>
          </div>
          
          <p style={{ marginTop: 16, marginBottom: 0 }}>
            Do you want to proceed with uploading this certificate?
          </p>
        </div>
      ),
      okText: 'Yes, Upload Certificate',
      cancelText: 'Cancel',
      okButtonProps: { 
        type: 'primary',
        style: { backgroundColor: PHOENICIAN_GOLD, borderColor: PHOENICIAN_GOLD }
      },
      onOk: async () => {
        try {
          // Actual upload
          const uploadResponse = await loamsEngineerApi.certificates.upload(formData);
          
          if (uploadResponse.success) {
            handleUploadSuccess(uploadResponse, onSuccess);
          } else {
            throw new Error(uploadResponse.error || 'Upload failed');
          }
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          message.error('Failed to upload certificate');
          onError(uploadError);
          setUploading(false);
          setPendingFile(null);
          setFileList([]);
        }
      },
      onCancel: () => {
        message.info('Upload cancelled');
        onError(new Error('Upload cancelled by user'));
        setUploading(false);
        setPendingFile(null);
        setFileList([]);
      }
    });
  };

  const handleUploadSuccess = (response, onSuccess) => {
    message.success('Certificate uploaded successfully!');
    onSuccess(response);
    fetchCertificates();
    setFileList([]);
    setUploading(false);
    setPendingFile(null);
    setValidationResult(null);
    
    if (response.invoice) {
      Modal.success({
        title: 'Upload Successful',
        content: (
          <div>
            <p>Invoice Number: <strong>{response.invoice.serial_number}</strong></p>
            <p>CC Reference: <strong>{response.invoice.cc}</strong></p>
            {response.validation?.performed && (
              <p style={{ color: '#52c41a' }}>
                <CheckCircleOutlined /> Validation passed with {Math.round((response.validation.confidence || 0) * 100)}% confidence
              </p>
            )}
          </div>
        )
      });
    }
  };

  const handlePreview = async (key) => {
    try {
      const response = await loamsEngineerApi.certificates.getPreviewUrl(key);
      if (response.success) {
        setPreviewUrl(response.url);
      }
    } catch (error) {
      console.error('Preview error:', error);
      message.error('Failed to load preview');
    }
  };

  const handleDownload = async (key, filename) => {
    try {
      await loamsEngineerApi.certificates.download(key, filename);
      message.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download certificate');
    }
  };

  // ============================================================================
  // TABLE COLUMNS
  // ============================================================================

  const agreementColumns = [
    {
      title: 'PO Number',
      dataIndex: 'ref_no',
      key: 'ref_no',
      width: 180,
      render: (text) => <Text strong copyable>{text}</Text>
    },
    {
      title: 'Service Type',
      dataIndex: 'service_type',
      key: 'service_type',
      width: 200,
      render: (_, record) => <ServiceTypeBadge serviceType={record.service_type} />
    },
    {
      title: 'Payee',
      dataIndex: 'payee',
      key: 'payee',
      ellipsis: true,
      width: 250
    },
    {
      title: 'Contract Period',
      key: 'period',
      width: 200,
      render: (_, record) => (
        <Text type="secondary">{record.start_date} - {record.end_date}</Text>
      )
    },
    {
      title: 'Months',
      dataIndex: 'months_count',
      key: 'months_count',
      width: 100,
      render: (count) => <Badge count={count} style={{ backgroundColor: PHOENICIAN_GOLD }} />
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button 
          type="primary"
          size="small"
          onClick={() => setSelectedAgreement(record.ref_no)}
          style={{ backgroundColor: PHOENICIAN_GOLD, borderColor: PHOENICIAN_GOLD }}
        >
          Select
        </Button>
      )
    }
  ];

  const certificateColumns = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      width: 120,
      render: (text) => <Tag icon={<CalendarOutlined />} color="blue">{text}</Tag>
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: () => <StatusTag exists={true} />
    },
    {
      title: 'Invoice',
      key: 'invoice',
      width: 150,
      render: (_, record) => (
        record.hasInvoice ? (
          <Tag color="green">{record.invoiceNumber}</Tag>
        ) : (
          <Tag color="orange">No Invoice</Tag>
        )
      )
    },
    {
      title: 'Size',
      dataIndex: 'sizeFormatted',
      key: 'size',
      width: 100
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Preview">
            <Button icon={<EyeOutlined />} size="small" onClick={() => handlePreview(record.key)} />
          </Tooltip>
          <Tooltip title="Download">
            <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownload(record.key, record.name)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  const bulkColumns = [
    {
      title: '',
      key: 'select',
      width: 50,
      render: (_, record) => (
        <Checkbox
          disabled={record.certificate_exists}
          checked={selectedBulkItems.includes(`${record.cluster}-${record.ref_no}`)}
          onChange={(e) => handleBulkItemSelect(`${record.cluster}-${record.ref_no}`, e.target.checked)}
        />
      )
    },
    {
      title: 'Cluster',
      dataIndex: 'cluster',
      key: 'cluster',
      ellipsis: true,
      width: 200
    },
    {
      title: 'PO Number',
      dataIndex: 'ref_no',
      key: 'ref_no',
      width: 150
    },
    {
      title: 'Service Type',
      key: 'service_type',
      width: 180,
      render: (_, record) => <ServiceTypeBadge serviceType={record.service_type} />
    },
    {
      title: 'Payee',
      dataIndex: 'payee',
      key: 'payee',
      ellipsis: true,
      width: 200
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => <StatusTag exists={record.certificate_exists} />
    }
  ];

  // ============================================================================
  // GENERATE AVAILABLE MONTHS FOR BULK
  // ============================================================================

  const getAvailableMonths = () => {
    const months = [];
    const currentDate = new Date();
    const yearInt = parseInt(selectedYear);
    
    for (let m = 1; m <= 12; m++) {
      const monthDate = new Date(yearInt, m - 1, 1);
      if (monthDate <= currentDate || yearInt < currentDate.getFullYear()) {
        months.push({
          value: `${yearInt}-${String(m).padStart(2, '0')}`,
          label: monthDate.toLocaleString('default', { month: 'long', year: 'numeric' })
        });
      }
    }
    
    return months;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Layout style={{ minHeight: '100vh' }} className="engineer-dashboard">
      {/* Sidebar */}
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        className="white-sidebar"
        style={{ 
          background: '#ffffff',
          boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
          borderRight: '1px solid #f0f0f0'
        }}
        width={260}
      >
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <img 
            src={phoenicianLogo} 
            alt="Phoenician Logo" 
            style={{ 
              height: collapsed ? '30px' : '50px',
              transition: 'all 0.3s'
            }} 
          />
          {!collapsed && (
            <Title level={5} style={{ color: PHOENICIAN_GOLD, marginTop: 10, marginBottom: 0 }}>
              LOAMS Dashboard
            </Title>
          )}
        </div>
        
        <Menu
          mode="inline"
          selectedKeys={[activeTab]}
          onClick={({ key }) => handleTabChange(key)}
          className="white-menu"
          style={{ 
            background: 'transparent', 
            marginTop: 20,
            border: 'none'
          }}
          items={[
            {
              key: 'single',
              icon: <CloudUploadOutlined style={{ fontSize: '18px', color: activeTab === 'single' ? PHOENICIAN_GOLD : '#666' }} />,
              label: <span style={{ color: activeTab === 'single' ? PHOENICIAN_GOLD : '#333' }}>Single Upload</span>,
            },
            {
              key: 'bulk',
              icon: <ThunderboltOutlined style={{ fontSize: '18px', color: activeTab === 'bulk' ? PHOENICIAN_GOLD : '#666' }} />,
              label: <span style={{ color: activeTab === 'bulk' ? PHOENICIAN_GOLD : '#333' }}>Bulk Generation</span>,
            },
            {
              key: 'progress',
              icon: <ProjectOutlined style={{ fontSize: '18px', color: activeTab === 'progress' ? PHOENICIAN_GOLD : '#666' }} />,
              label: <span style={{ color: activeTab === 'progress' ? PHOENICIAN_GOLD : '#333' }}>Work Progress</span>,
            },
            {
              key: 'help',
              icon: <QuestionCircleOutlined style={{ fontSize: '18px', color: activeTab === 'help' ? PHOENICIAN_GOLD : '#666' }} />,
              label: <span style={{ color: activeTab === 'help' ? PHOENICIAN_GOLD : '#333' }}>Help</span>,
            },
          ]}
        />
        
        {/* Year Filter in Sidebar */}
        {!collapsed && (
          <div style={{ padding: '20px', position: 'absolute', bottom: 60, width: '100%' }}>
            <Text style={{ color: '#666', display: 'block', marginBottom: 8 }}>
              <CalendarOutlined /> Year Filter
            </Text>
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ width: '100%' }}
              placeholder="Select Year"
            >
              {years.map(year => (
                <Option key={year} value={year.toString()}>{year}</Option>
              ))}
            </Select>
          </div>
        )}
      </Sider>

      <Layout>
        {/* Header */}
        <Header style={{ 
          background: '#fff', 
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Title level={4} style={{ margin: 0, color: PHOENICIAN_DARK }}>
              {activeTab === 'single' && 'Certificate Upload'}
              {activeTab === 'bulk' && 'Bulk Certificate Generation'}
              {activeTab === 'progress' && 'Work Progress Report'}
              {activeTab === 'help' && 'Help & Documentation'}
            </Title>
            <GPT4HealthIndicator status={gpt4Health} />
          </div>
          
          <Space>
            <Button icon={<ReloadOutlined />} onClick={initializeDashboard}>Refresh</Button>
          </Space>
        </Header>

        {/* Main Content */}
        <Content style={{ margin: '24px', background: '#f5f5f5' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '100px' }}>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: 16 }}>Loading dashboard...</Text>
            </div>
          ) : (
            <>
              {/* Tab Content */}
              {activeTab === 'single' && (
                <SingleUploadTab
                  clusters={clusters}
                  selectedCluster={selectedCluster}
                  setSelectedCluster={setSelectedCluster}
                  agreements={agreements}
                  selectedAgreement={selectedAgreement}
                  setSelectedAgreement={setSelectedAgreement}
                  agreementDetails={agreementDetails}
                  months={months}
                  selectedMonth={selectedMonth}
                  setSelectedMonth={setSelectedMonth}
                  certificates={certificates}
                  handleFileUpload={handleFileUpload}
                  uploading={uploading}
                  fileList={fileList}
                  setFileList={setFileList}
                  previewUrl={previewUrl}
                  handlePreview={handlePreview}
                  handleDownload={handleDownload}
                  agreementColumns={agreementColumns}
                  certificateColumns={certificateColumns}
                  PHOENICIAN_GOLD={PHOENICIAN_GOLD}
                />
              )}

              {activeTab === 'bulk' && (
                <BulkGenerationTab
                  selectedYear={selectedYear}
                  payees={payees}
                  selectedPayee={selectedPayee}
                  setSelectedPayee={setSelectedPayee}
                  bulkMonth={bulkMonth}
                  handleBulkMonthChange={handleBulkMonthChange}
                  getAvailableMonths={getAvailableMonths}
                  bulkAgreements={bulkAgreements}
                  bulkStatistics={bulkStatistics}
                  selectedBulkItems={selectedBulkItems}
                  handleBulkSelectAll={handleBulkSelectAll}
                  openBulkGenerationModal={openBulkGenerationModal}
                  bulkLoading={bulkLoading}
                  bulkColumns={bulkColumns}
                  PHOENICIAN_GOLD={PHOENICIAN_GOLD}
                />
              )}

              {activeTab === 'progress' && (
                <WorkProgress />
              )}

              {activeTab === 'help' && (
                <HelpTab PHOENICIAN_GOLD={PHOENICIAN_GOLD} />
              )}
            </>
          )}
        </Content>
      </Layout>

      {/* Preview Modal */}
      <Modal
        title="Certificate Preview"
        open={!!previewUrl}
        onCancel={() => setPreviewUrl(null)}
        width={900}
        footer={null}
        destroyOnClose
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '70vh', border: 'none' }}
            title="Certificate Preview"
          />
        )}
      </Modal>

      {/* Bulk Generation Modal */}
      <BulkCertificateGenerator
        visible={bulkModalVisible}
        onClose={closeBulkModal}
        agreements={getSelectedAgreementsData()}
        month={bulkMonth}
        onComplete={handleBulkGenerationComplete}
      />
    </Layout>
  );
};

// ============================================================================
// SUB-COMPONENTS (SingleUploadTab, BulkGenerationTab, HelpTab remain the same)
// ============================================================================

const SingleUploadTab = ({
  clusters,
  selectedCluster,
  setSelectedCluster,
  agreements,
  selectedAgreement,
  setSelectedAgreement,
  agreementDetails,
  months,
  selectedMonth,
  setSelectedMonth,
  certificates,
  handleFileUpload,
  uploading,
  fileList,
  setFileList,
  previewUrl,
  handlePreview,
  handleDownload,
  agreementColumns,
  certificateColumns,
  PHOENICIAN_GOLD,
  // ADD these new props from the parent:
  overviewStats
}) => {
  return (
    <>
      {/* ===== OVERVIEW STATS ROW ===== */}
      <div className="overview-stats-row">
        <div className="overview-stat-card gold">
          <div className="stat-icon">
            <FolderOutlined />
          </div>
          <div className="stat-label">Total Clusters</div>
          <div className="stat-value">{overviewStats?.totalClusters || clusters.length || 0}</div>
          <div className="stat-subtext">Active in {new Date().getFullYear()}</div>
        </div>
        <div className="overview-stat-card blue">
          <div className="stat-icon">
            <FileTextOutlined />
          </div>
          <div className="stat-label">Total POs</div>
          <div className="stat-value">{overviewStats?.totalPOs || 0}</div>
          <div className="stat-subtext">Service agreements</div>
        </div>
        <div className="overview-stat-card green">
          <div className="stat-icon">
            <CheckCircleOutlined />
          </div>
          <div className="stat-label">Certificates</div>
          <div className="stat-value">{overviewStats?.totalCertificates || 0}</div>
          <div className="stat-subtext">Uploaded this year</div>
        </div>
        <div className="overview-stat-card amber">
          <div className="stat-icon">
            <BarChartOutlined />
          </div>
          <div className="stat-label">Completion</div>
          <div className="stat-value">{overviewStats?.completionRate || 0}%</div>
          <div className="stat-subtext">Overall progress</div>
        </div>
      </div>
 
      {/* ===== MAIN CONTENT ===== */}
      <Row gutter={[20, 20]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <CloudUploadOutlined style={{ color: PHOENICIAN_GOLD }} />
                <span>Upload Certificate</span>
              </Space>
            }
            extra={<Badge status="processing" text="Single Upload" />}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Cluster Select */}
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '12px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <FolderOutlined /> Select Cluster
                </Text>
                <Select
                  showSearch
                  value={selectedCluster}
                  onChange={(value) => {
                    setSelectedCluster(value);
                    setSelectedAgreement('');
                    setSelectedMonth('');
                  }}
                  placeholder="Search and select cluster..."
                  style={{ width: '100%' }}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  size="large"
                >
                  {clusters.map(cluster => <Option key={cluster} value={cluster}>{cluster}</Option>)}
                </Select>
              </div>
 
              {/* Agreement Select */}
              {selectedCluster && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '12px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <FileTextOutlined /> Select Agreement (PO)
                  </Text>
                  <Select
                    showSearch
                    value={selectedAgreement}
                    onChange={(value) => {
                      setSelectedAgreement(value);
                      setSelectedMonth('');
                    }}
                    placeholder="Select agreement..."
                    style={{ width: '100%' }}
                    size="large"
                    optionLabelProp="label"
                  >
                    {agreements.map(agreement => (
                      <Option key={agreement.ref_no} value={agreement.ref_no} label={agreement.ref_no}>
                        <div>
                          <Text strong>{agreement.ref_no}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>{agreement.formatted_service_type}</Text>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </div>
              )}
 
              {/* Agreement Details Card */}
              {agreementDetails && (
                <Card 
                  size="small" 
                  style={{ 
                    background: 'linear-gradient(135deg, #FBF6EF 0%, #FFFFFF 100%)', 
                    borderLeft: '4px solid #A2732E' 
                  }}
                >
                  <Row gutter={[16, 12]}>
                    <Col span={12}>
                      <Text style={{ fontSize: '11px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Payee</Text>
                      <br />
                      <Text strong style={{ fontSize: '13px' }}>{agreementDetails.payee || '-'}</Text>
                    </Col>
                    <Col span={12}>
                      <Text style={{ fontSize: '11px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Service Type</Text>
                      <br />
                      <ServiceTypeBadge serviceType={agreementDetails.service_type} />
                    </Col>
                    <Col span={12}>
                      <Text style={{ fontSize: '11px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Start Date</Text>
                      <br />
                      <Text style={{ fontSize: '13px' }}>{agreementDetails.start_date || '-'}</Text>
                    </Col>
                    <Col span={12}>
                      <Text style={{ fontSize: '11px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>End Date</Text>
                      <br />
                      <Text style={{ fontSize: '13px' }}>{agreementDetails.end_date || '-'}</Text>
                    </Col>
                  </Row>
                </Card>
              )}
 
              {/* Month Select */}
              {selectedAgreement && months.length > 0 && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '12px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <CalendarOutlined /> Select Month
                  </Text>
                  <Select
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    placeholder="Select month..."
                    style={{ width: '100%' }}
                    size="large"
                  >
                    {months.map(month => {
                      const hasExisting = certificates.some(c => c.month === month.value);
                      return (
                        <Option key={month.value} value={month.value}>
                          <Space>
                            <span>{month.label}</span>
                            {hasExisting && <CheckCircleOutlined style={{ color: '#10B981' }} />}
                          </Space>
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              )}
 
              {/* Upload Zone */}
              {selectedMonth && (
                <Dragger
                  customRequest={handleFileUpload}
                  accept=".pdf"
                  maxCount={1}
                  fileList={fileList}
                  onChange={({ fileList }) => setFileList(fileList)}
                  disabled={uploading}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: PHOENICIAN_GOLD, fontSize: '42px' }} />
                  </p>
                  <p className="ant-upload-text">Click or drag PDF certificate to upload</p>
                  <p className="ant-upload-hint">Only PDF files accepted. Max size: 10MB</p>
                </Dragger>
              )}
            </Space>
          </Card>
 
          {/* Existing Certificates */}
          {selectedAgreement && certificates.length > 0 && (
            <Card 
              title={
                <Space>
                  <FilePdfOutlined style={{ color: PHOENICIAN_GOLD }} />
                  <span>Existing Certificates ({certificates.length})</span>
                </Space>
              }
              style={{ marginTop: 20 }}
            >
              <Table 
                dataSource={certificates} 
                columns={certificateColumns} 
                rowKey="key" 
                size="small" 
                pagination={{ pageSize: 5 }} 
              />
            </Card>
          )}
        </Col>
 
        {/* Right Column */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <EyeOutlined style={{ color: PHOENICIAN_GOLD }} />
                <span>Preview</span>
              </Space>
            } 
            style={{ minHeight: 400 }}
          >
            {previewUrl ? (
              <iframe 
                src={previewUrl} 
                style={{ width: '100%', height: '500px', border: 'none', borderRadius: '8px' }} 
                title="Certificate Preview" 
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: '300px',
                background: '#F8FAFC',
                borderRadius: '10px',
                border: '1px dashed #E2E8F0'
              }}>
                <FilePdfOutlined style={{ fontSize: '48px', color: '#CBD5E1', marginBottom: '12px' }} />
                <Text style={{ color: '#94A3B8', fontSize: '14px', fontWeight: 500 }}>
                  Select a certificate to preview
                </Text>
              </div>
            )}
          </Card>
 
          {/* Agreements Table */}
          {selectedCluster && agreements.length > 0 && (
            <Card 
              title={
                <Space>
                  <FileTextOutlined style={{ color: PHOENICIAN_GOLD }} />
                  <span>Agreements for {selectedCluster}</span>
                </Space>
              }
              style={{ marginTop: 20 }}
            >
              <Table 
                dataSource={agreements} 
                columns={agreementColumns} 
                rowKey="ref_no" 
                size="small" 
                pagination={{ pageSize: 5 }} 
                scroll={{ x: 800 }} 
              />
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

const BulkGenerationTab = ({
  selectedYear,
  payees,
  selectedPayee,
  setSelectedPayee,
  bulkMonth,
  handleBulkMonthChange,
  getAvailableMonths,
  bulkAgreements,
  bulkStatistics,
  selectedBulkItems,
  handleBulkSelectAll,
  openBulkGenerationModal,
  bulkLoading,
  bulkColumns,
  PHOENICIAN_GOLD
}) => {
  return (
    <>
      {/* ===== BULK STATS ROW ===== */}
      {bulkStatistics && (
        <div className="overview-stats-row">
          <div className="overview-stat-card gold">
            <div className="stat-icon"><FolderOutlined /></div>
            <div className="stat-label">Total Agreements</div>
            <div className="stat-value">{bulkStatistics.total}</div>
            <div className="stat-subtext">For {bulkMonth || 'selected month'}</div>
          </div>
          <div className="overview-stat-card green">
            <div className="stat-icon"><CheckCircleOutlined /></div>
            <div className="stat-label">With Certificates</div>
            <div className="stat-value">{bulkStatistics.with_certificates}</div>
            <div className="stat-subtext">Already uploaded</div>
          </div>
          <div className="overview-stat-card amber">
            <div className="stat-icon"><CloseCircleOutlined /></div>
            <div className="stat-label">Missing</div>
            <div className="stat-value">{bulkStatistics.without_certificates}</div>
            <div className="stat-subtext">Need generation</div>
          </div>
          <div className="overview-stat-card blue">
            <div className="stat-icon"><BarChartOutlined /></div>
            <div className="stat-label">Completion</div>
            <div className="stat-value">{bulkStatistics.completion_rate}%</div>
            <div className="stat-subtext">
              <Progress percent={bulkStatistics.completion_rate} showInfo={false} strokeColor={PHOENICIAN_GOLD} size="small" style={{ width: 100, marginTop: 4 }} />
            </div>
          </div>
        </div>
      )}
 
      {/* Controls */}
      <Row gutter={[20, 20]}>
        <Col span={24}>
          <Card>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} lg={6}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '12px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <CalendarOutlined /> Select Month
                </Text>
                <Select
                  value={bulkMonth}
                  onChange={handleBulkMonthChange}
                  placeholder="Select month..."
                  style={{ width: '100%' }}
                  size="large"
                >
                  {getAvailableMonths().map(month => (
                    <Option key={month.value} value={month.value}>{month.label}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={8} lg={6}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '12px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <FilterOutlined /> Filter by Payee
                </Text>
                <Select
                  value={selectedPayee}
                  onChange={setSelectedPayee}
                  placeholder="All payees..."
                  style={{ width: '100%' }}
                  size="large"
                  allowClear
                  showSearch
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {payees.map(p => (
                    <Option key={p.payee} value={p.payee}>{p.payee} ({p.po_count} POs)</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={8} lg={12}>
                <div style={{ marginTop: 22, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Checkbox onChange={handleBulkSelectAll} disabled={!bulkAgreements.length}>
                    Select All Missing
                  </Checkbox>
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    onClick={openBulkGenerationModal}
                    disabled={selectedBulkItems.length === 0}
                    loading={bulkLoading}
                    style={{ backgroundColor: PHOENICIAN_GOLD, borderColor: PHOENICIAN_GOLD }}
                    size="large"
                  >
                    Generate ({selectedBulkItems.length})
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
 
        {/* Agreements Table */}
        <Col span={24}>
          <Card 
            title={
              <Space>
                <FileTextOutlined style={{ color: PHOENICIAN_GOLD }} />
                <span>Agreements for {bulkMonth || 'Selected Month'}</span>
              </Space>
            }
          >
            <Table
              dataSource={bulkAgreements}
              columns={bulkColumns}
              rowKey={(record) => `${record.cluster}-${record.ref_no}`}
              loading={bulkLoading}
              pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}` }}
              scroll={{ x: 1200 }}
              rowClassName={(record) => record.certificate_exists ? 'row-success' : 'row-warning'}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

const HelpTab = ({ PHOENICIAN_GOLD }) => {
  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card title={<Space><QuestionCircleOutlined style={{ color: PHOENICIAN_GOLD }} /><span>Help & Documentation</span></Space>}>
          <Tabs defaultActiveKey="1">
            <TabPane tab="Getting Started" key="1">
              <Title level={4}>Welcome to LOAMS Certificate Dashboard</Title>
              <Paragraph>
                This dashboard allows you to manage completion certificates for LOAMS contracts.
                You can upload individual certificates, generate bulk certificates, and view work progress.
              </Paragraph>
              
              <Divider />
              
              <Title level={5}>Single Upload</Title>
              <Paragraph>
                <ol>
                  <li>Select a <strong>Year</strong> from the sidebar filter</li>
                  <li>Select a <strong>Cluster</strong> from the dropdown</li>
                  <li>Select an <strong>Agreement (PO Number)</strong></li>
                  <li>Select the <strong>Month</strong> for the certificate</li>
                  <li>Upload the PDF certificate file</li>
                </ol>
              </Paragraph>
              
              <Alert
                message="AI Validation"
                description="Certificates are automatically validated using GPT-4 Vision to ensure the PO number and month match."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </TabPane>
            
            <TabPane tab="Bulk Generation" key="2">
              <Title level={5}>Bulk Certificate Generation</Title>
              <Paragraph>The bulk generation feature allows you to generate multiple certificates at once.</Paragraph>
              
              <Paragraph>
                <ol>
                  <li>Select a <strong>Month</strong> for bulk operations</li>
                  <li>Optionally filter by <strong>Payee</strong></li>
                  <li>The system will show all agreements valid for that month</li>
                  <li>Check <strong>"Select All Missing"</strong> or manually select agreements</li>
                  <li>Click <strong>Generate</strong> to open the generation modal</li>
                </ol>
              </Paragraph>
            </TabPane>

            <TabPane tab="Work Progress" key="3">
              <Title level={5}>Work Progress Report</Title>
              <Paragraph>
                The Work Progress section shows an Excel-like overview of all POs and their certificate status for each month.
              </Paragraph>
              
              <Paragraph>
                <ul>
                  <li><strong>Yellow cells</strong>: Month is active for this PO (contract covers this month)</li>
                  <li><strong>Green cells with checkmark</strong>: Certificate uploaded for this month</li>
                  <li><strong>Gray cells</strong>: Month not applicable (outside contract period)</li>
                </ul>
              </Paragraph>
              
              <Alert
                message="2023-2024 Data"
                description="For years 2023-2024, certificates are marked as uploaded if an invoice exists for that month (certificates exist but may not be in S3)."
                type="info"
                showIcon
              />
            </TabPane>
            
            <TabPane tab="Data Sources" key="4">
              <Title level={5}>Database Tables</Title>
              <Paragraph>This system uses two database tables:</Paragraph>
              
              <Card size="small" style={{ marginBottom: 16 }}>
                <Title level={5}>pts_invoices (2023-2024)</Title>
                <Text type="secondary">
                  Contains older contract data with fields: project, agreement_ref_no, 
                  contract_start_date, contract_finish_date, service_type, payee
                </Text>
              </Card>
              
              <Card size="small">
                <Title level={5}>pts_invoice_details (2025+)</Title>
                <Text type="secondary">
                  Contains newer contract data with additional fields: accounts_name, po_no,
                  start_date, end_date, auxillarsss, address, trn, contract_value
                </Text>
              </Card>
            </TabPane>
          </Tabs>
        </Card>
      </Col>
    </Row>
  );
};

export default EngineerDashboard;